import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PromotionForm from '../../components/admin/PromotionForm.jsx';

const PromotionsManagement = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPromotions, setTotalPromotions] = useState(0);
  const promotionsPerPage = 10;

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 480);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 480 && window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      setIsSmallScreen(window.innerWidth < 480);
      setIsTablet(window.innerWidth >= 480 && window.innerWidth < 1024);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch promotions
  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * promotionsPerPage;
      
      const response = await fetch(
        `http://localhost:5000/api/promotions?status=${statusFilter}&type=${typeFilter}&limit=${promotionsPerPage}&skip=${skip}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch promotions');
      
      const data = await response.json();
      const promotionsList = data.data || [];
      setPromotions(promotionsList);
      
      // Calculate pagination
      const totalPromos = data.total || promotionsList.length + (currentPage - 1) * promotionsPerPage;
      setTotalPromotions(totalPromos);
      setTotalPages(Math.ceil(totalPromos / promotionsPerPage));
      
    } catch (error) {
      console.error('Error fetching promotions:', error);
      toast.error('Failed to load promotions');
      setPromotions([]);
      setTotalPromotions(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromotions();
  }, [statusFilter, typeFilter, currentPage]);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = isSmallScreen ? 3 : (isMobile ? 5 : 7);
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust if we're at the beginning
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    return pageNumbers;
  };

  // Delete promotion
  const handleDeletePromotion = async (promotionId) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      try {
        const response = await fetch(`http://localhost:5000/api/promotions/${promotionId}`, {
          method: 'DELETE',
        });
        
        if (!response.ok) throw new Error('Failed to delete promotion');
        
        toast.success('Promotion deleted successfully');
        fetchPromotions();
      } catch (error) {
        console.error('Error deleting promotion:', error);
        toast.error('Failed to delete promotion');
      }
    }
  };

  // Update promotion status
  const handleStatusUpdate = async (promotionId, newStatus) => {
    try {
      const response = await fetch(`http://localhost:5000/api/promotions/${promotionId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });
      
      if (!response.ok) throw new Error('Failed to update status');
      
      toast.success(`Promotion ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchPromotions();
    } catch (error) {
      console.error('Error updating promotion status:', error);
      toast.error('Failed to update promotion status');
    }
  };

  // Edit promotion
  const handleEditPromotion = (promotion) => {
    setEditingPromotion(promotion);
    setShowForm(true);
  };

  // Add new promotion
  const handleAddPromotion = () => {
    setEditingPromotion(null);
    setShowForm(true);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'var(--success)';
      case 'scheduled': return 'var(--info)';
      case 'inactive': return 'var(--warning)';
      case 'expired': return 'var(--error)';
      default: return 'var(--text-light)';
    }
  };

  // Get status display for mobile
  const getStatusDisplay = (status, isSmallScreen = false) => {
    const statusMap = {
      'active': { text: 'Active', short: 'Act' },
      'scheduled': { text: 'Scheduled', short: 'Sch' },
      'inactive': { text: 'Inactive', short: 'Ina' },
      'expired': { text: 'Expired', short: 'Exp' }
    };
    
    const statusInfo = statusMap[status] || { 
      text: status || 'Unknown', 
      short: status?.substring(0, 3) || 'Unk' 
    };
    
    return isSmallScreen ? statusInfo.short : statusInfo.text;
  };

  // Get type icon
  const getTypeIcon = (type) => {
    switch (type) {
      case 'banner': return '🖼️';
      case 'flash_sale': return '⚡';
      case 'seasonal': return '🎄';
      case 'special_offer': return '🎁';
      case 'black_friday': return '🖤';
      default: return '📢';
    }
  };

  // Get type display
  const getTypeDisplay = (type) => {
    return type ? type.replace('_', ' ') : 'Promotion';
  };

  // Check if promotion is currently active
  const isCurrentlyActive = (promotion) => {
    const now = new Date();
    return promotion.status === 'active' && 
           new Date(promotion.startDate) <= now && 
           new Date(promotion.endDate) >= now;
  };

  // Format date - RESPONSIVE
  const formatDate = (dateString) => {
    try {
      if (isSmallScreen) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'numeric',
          day: 'numeric'
        });
      }
      if (isMobile) {
        return new Date(dateString).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric'
        });
      }
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Get discount display
  const getDiscountDisplay = (promotion) => {
    if (promotion.discountType === 'percentage') {
      return `${promotion.discountValue}% OFF`;
    }
    if (promotion.discountType === 'fixed') {
      return `KSh ${promotion.discountValue} OFF`;
    }
    if (promotion.discountType === 'free_shipping') {
      return 'FREE SHIPPING';
    }
    if (promotion.discountType === 'bogo') {
      return 'BUY 1 GET 1';
    }
    return 'No Discount';
  };

  return (
    <div className="admin-content" style={{ 
      padding: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-4)' : 'var(--space-6)'),
      maxWidth: '100%',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <div style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)'),
        paddingBottom: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
        borderBottom: '1px solid var(--border-light)'
      }}>
        <h1 className="accent-gold" style={{ 
          fontSize: isSmallScreen ? 'var(--font-size-lg)' : (isMobile ? 'var(--font-size-xl)' : 'var(--font-size-3xl)'),
          marginBottom: isSmallScreen ? 'var(--space-1)' : (isMobile ? 'var(--space-2)' : 'var(--space-3)')
        }}>
          Promotions <span style={{ color: 'var(--text-dark)' }}>Management</span>
        </h1>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: isSmallScreen ? 'column' : (isMobile ? 'column' : 'row'),
          alignItems: isSmallScreen ? 'flex-start' : (isMobile ? 'flex-start' : 'center'),
          gap: isSmallScreen ? 'var(--space-2)' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
          justifyContent: 'space-between'
        }}>
          <p style={{ 
            color: 'var(--text-medium)',
            fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)')
          }}>
            Manage banners, flash sales, and promotional campaigns
          </p>
          
          <button 
            className="btn btn-gold"
            onClick={handleAddPromotion}
            style={{ 
              padding: isSmallScreen ? '6px 10px' : (isMobile ? 'var(--space-2)' : 'var(--space-3) var(--space-4)'),
              fontSize: isSmallScreen ? 'var(--font-size-xs)' : (isMobile ? 'var(--font-size-sm)' : 'var(--font-size-base)'),
              whiteSpace: 'nowrap'
            }}
          >
            <span>+</span>
            {isSmallScreen ? 'New Promotion' : 'Create Promotion'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card" style={{ 
        marginBottom: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-5)' : 'var(--space-6)') 
      }}>
        <div className="card-body" style={{
          padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: isSmallScreen ? 'column' : (isMobile ? 'column' : 'row'),
            gap: isSmallScreen ? '12px' : (isMobile ? 'var(--space-3)' : 'var(--space-4)'),
            alignItems: isSmallScreen ? 'stretch' : (isMobile ? 'stretch' : 'center')
          }}>
            {/* Status Filter */}
            <div className="form-group" style={{ 
              margin: 0,
              flex: isSmallScreen ? '1' : (isMobile ? '1' : 'none'),
              minWidth: isMobile ? '100%' : '200px'
            }}>
              <label className="form-label" style={{
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                marginBottom: '4px',
                display: 'block'
              }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-select"
                style={{
                  width: '100%',
                  padding: isSmallScreen ? '8px 12px' : '10px 16px',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="scheduled">Scheduled</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            {/* Type Filter */}
            <div className="form-group" style={{ 
              margin: 0,
              flex: isSmallScreen ? '1' : (isMobile ? '1' : 'none'),
              minWidth: isMobile ? '100%' : '200px'
            }}>
              <label className="form-label" style={{
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                marginBottom: '4px',
                display: 'block'
              }}>
                Type
              </label>
              <select
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-select"
                style={{
                  width: '100%',
                  padding: isSmallScreen ? '8px 12px' : '10px 16px',
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                }}
              >
                <option value="all">All Types</option>
                <option value="banner">Banner</option>
                <option value="flash_sale">Flash Sale</option>
                <option value="seasonal">Seasonal</option>
                <option value="special_offer">Special Offer</option>
                <option value="black_friday">Black Friday</option>
              </select>
            </div>

            <button 
              onClick={fetchPromotions}
              className="btn btn-secondary"
              style={{ 
                alignSelf: isSmallScreen ? 'stretch' : 'flex-end',
                padding: isSmallScreen ? '8px 12px' : '10px 16px',
                fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                height: isSmallScreen ? '40px' : 'auto',
                marginTop: isSmallScreen ? '0' : 'auto'
              }}
            >
              {isSmallScreen ? 'Refresh' : '🔄 Refresh'}
            </button>
          </div>
        </div>
      </div>

      {/* Promotions Grid - DESKTOP (unchanged from original) */}
      {!isMobile ? (
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : (isMobile ? 'var(--space-3) var(--space-4)' : 'var(--space-4) var(--space-5)')
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : (isMobile ? 'var(--font-size-base)' : 'var(--font-size-lg)'),
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <span style={{ color: 'var(--accent-gold)' }}>📢</span>
              All Promotions ({totalPromotions})
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : (isMobile ? 'var(--space-4)' : 'var(--space-5)')
          }}>
            {loading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
                color: 'var(--text-light)'
              }}>
                Loading promotions...
              </div>
            ) : promotions.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
                color: 'var(--text-light)'
              }}>
                <p style={{ fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-base)' }}>
                  No promotions found.
                </p>
                <button 
                  className="btn btn-gold"
                  onClick={handleAddPromotion}
                  style={{ 
                    marginTop: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                    padding: isSmallScreen ? '8px 12px' : '12px 24px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                  }}
                >
                  Create Your First Promotion
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Grid Layout - EXACTLY as original */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: 'var(--space-5)'
                }}>
                  {promotions.map((promotion) => (
                    <div 
                      key={promotion._id}
                      className="card"
                      style={{ 
                        border: isCurrentlyActive(promotion) ? '2px solid var(--accent-gold)' : '1px solid var(--border-light)',
                        position: 'relative'
                      }}
                    >
                      {/* Promotion Header */}
                      <div style={{
                        padding: 'var(--space-4)',
                        borderBottom: '1px solid var(--border-light)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start'
                      }}>
                        <div>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-2)'
                          }}>
                            <span style={{ fontSize: '1.2rem' }}>
                              {getTypeIcon(promotion.type)}
                            </span>
                            <h4 style={{ 
                              margin: 0, 
                              color: 'var(--text-dark)',
                              fontSize: 'var(--font-size-lg)'
                            }}>
                              {promotion.title}
                            </h4>
                          </div>
                          <p style={{ 
                            margin: 0, 
                            color: 'var(--text-light)',
                            fontSize: 'var(--font-size-sm)'
                          }}>
                            {promotion.description}
                          </p>
                        </div>
                        
                        <span 
                          style={{ 
                            backgroundColor: getStatusColor(promotion.status),
                            color: 'white',
                            padding: 'var(--space-1) var(--space-2)',
                            borderRadius: 'var(--radius-sm)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: '600',
                            textTransform: 'capitalize'
                          }}
                        >
                          {promotion.status}
                          {isCurrentlyActive(promotion) && ' 🔥'}
                        </span>
                      </div>

                      {/* Promotion Image */}
                      {promotion.bannerImage?.url && (
                        <div style={{
                          padding: 'var(--space-4)',
                          borderBottom: '1px solid var(--border-light)'
                        }}>
                          <img 
                            src={promotion.bannerImage.url} 
                            alt={promotion.bannerImage.alt || promotion.title}
                            style={{
                              width: '100%',
                              height: '120px',
                              objectFit: 'cover',
                              borderRadius: 'var(--radius-md)'
                            }}
                          />
                        </div>
                      )}

                      {/* Promotion Details */}
                      <div style={{ padding: 'var(--space-4)' }}>
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: '1fr 1fr', 
                          gap: 'var(--space-3)',
                          marginBottom: 'var(--space-4)'
                        }}>
                          <div>
                            <div style={{ 
                              fontSize: 'var(--font-size-sm)', 
                              color: 'var(--text-light)',
                              marginBottom: 'var(--space-1)'
                            }}>
                              Type
                            </div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--text-dark)',
                              textTransform: 'capitalize'
                            }}>
                              {getTypeDisplay(promotion.type)}
                            </div>
                          </div>
                          
                          <div>
                            <div style={{ 
                              fontSize: 'var(--font-size-sm)', 
                              color: 'var(--text-light)',
                              marginBottom: 'var(--space-1)'
                            }}>
                              Position
                            </div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--text-dark)',
                              textTransform: 'capitalize'
                            }}>
                              {promotion.position?.replace('_', ' ') || 'Default'}
                            </div>
                          </div>
                          
                          <div>
                            <div style={{ 
                              fontSize: 'var(--font-size-sm)', 
                              color: 'var(--text-light)',
                              marginBottom: 'var(--space-1)'
                            }}>
                              Discount
                            </div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--success)'
                            }}>
                              {getDiscountDisplay(promotion)}
                            </div>
                          </div>
                          
                          <div>
                            <div style={{ 
                              fontSize: 'var(--font-size-sm)', 
                              color: 'var(--text-light)',
                              marginBottom: 'var(--space-1)'
                            }}>
                              Priority
                            </div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--text-dark)'
                            }}>
                              {promotion.priority}
                            </div>
                          </div>
                        </div>

                        {/* Dates */}
                        <div style={{ 
                          padding: 'var(--space-3)',
                          backgroundColor: 'var(--bg-light)',
                          borderRadius: 'var(--radius-md)',
                          marginBottom: 'var(--space-4)'
                        }}>
                          <div style={{ 
                            fontSize: 'var(--font-size-sm)', 
                            color: 'var(--text-light)',
                            marginBottom: 'var(--space-2)'
                          }}>
                            Campaign Period
                          </div>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr', 
                            gap: 'var(--space-2)',
                            fontSize: 'var(--font-size-sm)'
                          }}>
                            <div>
                              <strong>Start:</strong> {formatDate(promotion.startDate)}
                            </div>
                            <div>
                              <strong>End:</strong> {formatDate(promotion.endDate)}
                            </div>
                          </div>
                        </div>

                        {/* Stats */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-light)',
                          marginBottom: 'var(--space-4)'
                        }}>
                          <div>
                            <strong>{promotion.clicks || 0}</strong> Clicks
                          </div>
                          <div>
                            <strong>{promotion.impressions || 0}</strong> Views
                          </div>
                          <div>
                            <strong>{promotion.usedCount || 0}</strong> Used
                          </div>
                        </div>

                        {/* Actions */}
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <button 
                            className="btn btn-secondary"
                            style={{ flex: 1, padding: 'var(--space-2)' }}
                            onClick={() => handleEditPromotion(promotion)}
                          >
                            Edit
                          </button>
                          
                          {promotion.status === 'active' ? (
                            <button 
                              className="btn btn-warning"
                              style={{ padding: 'var(--space-2)' }}
                              onClick={() => handleStatusUpdate(promotion._id, 'inactive')}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button 
                              className="btn btn-success"
                              style={{ padding: 'var(--space-2)' }}
                              onClick={() => handleStatusUpdate(promotion._id, 'active')}
                            >
                              Activate
                            </button>
                          )}
                          
                          <button 
                            className="btn btn-danger"
                            style={{ padding: 'var(--space-2)' }}
                            onClick={() => handleDeletePromotion(promotion._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          
          {/* Desktop Pagination */}
          {!isMobile && totalPages > 1 && (
            <div className="card-footer" style={{
              padding: 'var(--space-4) var(--space-5)',
              borderTop: '1px solid var(--border-light)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text-medium)'
              }}>
                Showing {(currentPage - 1) * promotionsPerPage + 1} to {Math.min(currentPage * promotionsPerPage, totalPromotions)} of {totalPromotions} promotions
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}>
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 12px',
                    fontSize: 'var(--font-size-sm)',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  First
                </button>

                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 12px',
                    fontSize: 'var(--font-size-sm)',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>

                <div style={{
                  display: 'flex',
                  gap: '4px'
                }}>
                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={pageNum === currentPage ? 'btn btn-gold' : 'btn btn-secondary'}
                      style={{
                        padding: '8px 12px',
                        fontSize: 'var(--font-size-sm)',
                        minWidth: '40px',
                        fontWeight: pageNum === currentPage ? '600' : '400'
                      }}
                    >
                      {pageNum}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 12px',
                    fontSize: 'var(--font-size-sm)',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next
                </button>

                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                  className="btn btn-secondary"
                  style={{
                    padding: '8px 12px',
                    fontSize: 'var(--font-size-sm)',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* MOBILE VIEW - Enhanced with all information */
        <div className="card card-gold">
          <div className="card-header" style={{
            padding: isSmallScreen ? 'var(--space-2) var(--space-3)' : 'var(--space-3) var(--space-4)'
          }}>
            <h3 style={{ 
              margin: 0,
              fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}>
              <span style={{ color: 'var(--accent-gold)' }}>📢</span>
              All Promotions ({totalPromotions})
            </h3>
          </div>
          
          <div className="card-body" style={{
            padding: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)'
          }}>
            {loading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
                color: 'var(--text-light)'
              }}>
                Loading promotions...
              </div>
            ) : promotions.length === 0 ? (
              <div style={{ 
                textAlign: 'center', 
                padding: isSmallScreen ? 'var(--space-4)' : 'var(--space-8)', 
                color: 'var(--text-light)'
              }}>
                <p style={{ fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-base)' }}>
                  No promotions found.
                </p>
                <button 
                  className="btn btn-gold"
                  onClick={handleAddPromotion}
                  style={{ 
                    marginTop: isSmallScreen ? 'var(--space-2)' : 'var(--space-4)',
                    padding: isSmallScreen ? '8px 12px' : '12px 24px',
                    fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)'
                  }}
                >
                  Create Your First Promotion
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: isSmallScreen ? '12px' : 'var(--space-3)' }}>
                {promotions.map((promotion) => (
                  <div 
                    key={promotion._id}
                    className="card"
                    style={{ 
                      border: isCurrentlyActive(promotion) ? '2px solid var(--accent-gold)' : '1px solid var(--border-light)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Mobile Promotion Header with Image */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}>
                      {/* Image - Always show on mobile */}
                      {promotion.bannerImage?.url && (
                        <div style={{
                          position: 'relative',
                          height: isSmallScreen ? '120px' : '150px',
                          overflow: 'hidden'
                        }}>
                          <img 
                            src={promotion.bannerImage.url} 
                            alt={promotion.bannerImage.alt || promotion.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                          {/* Status badge overlay */}
                          <span 
                            style={{ 
                              position: 'absolute',
                              top: '10px',
                              right: '10px',
                              backgroundColor: getStatusColor(promotion.status),
                              color: 'white',
                              padding: '4px 8px',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)',
                              fontWeight: '600',
                              textTransform: 'capitalize',
                              zIndex: 1
                            }}
                          >
                            {getStatusDisplay(promotion.status, isSmallScreen)}
                            {isCurrentlyActive(promotion) && ' 🔥'}
                          </span>
                        </div>
                      )}
                      
                      {/* Promotion Info */}
                      <div style={{
                        padding: isSmallScreen ? '12px' : 'var(--space-3)'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '8px',
                          marginBottom: isSmallScreen ? '8px' : 'var(--space-2)'
                        }}>
                          <span style={{ 
                            fontSize: isSmallScreen ? '1rem' : '1.1rem',
                            flexShrink: 0
                          }}>
                            {getTypeIcon(promotion.type)}
                          </span>
                          <div style={{ flex: 1 }}>
                            <h4 style={{ 
                              margin: 0, 
                              color: 'var(--text-dark)',
                              fontSize: isSmallScreen ? 'var(--font-size-sm)' : 'var(--font-size-base)',
                              lineHeight: 1.3,
                              marginBottom: '4px'
                            }}>
                              {promotion.title}
                            </h4>
                            {promotion.description && (
                              <p style={{ 
                                margin: 0, 
                                color: 'var(--text-light)',
                                fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)',
                                lineHeight: 1.4
                              }}>
                                {promotion.description}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Quick Stats Row */}
                        <div style={{ 
                          display: 'grid', 
                          gridTemplateColumns: 'repeat(3, 1fr)',
                          gap: '8px',
                          marginBottom: isSmallScreen ? '12px' : 'var(--space-3)',
                          fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)'
                        }}>
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                              color: 'var(--text-light)',
                              marginBottom: '2px'
                            }}>
                              Type
                            </div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--text-dark)'
                            }}>
                              {getTypeDisplay(promotion.type)}
                            </div>
                          </div>
                          
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                              color: 'var(--text-light)',
                              marginBottom: '2px'
                            }}>
                              Discount
                            </div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--success)'
                            }}>
                              {promotion.discountType === 'percentage' && `${promotion.discountValue}%`}
                              {promotion.discountType === 'fixed' && `KSh ${promotion.discountValue}`}
                              {promotion.discountType === 'free_shipping' && 'FREE'}
                              {promotion.discountType === 'bogo' && 'BOGO'}
                            </div>
                          </div>
                          
                          <div style={{ textAlign: 'center' }}>
                            <div style={{ 
                              color: 'var(--text-light)',
                              marginBottom: '2px'
                            }}>
                              Priority
                            </div>
                            <div style={{ 
                              fontWeight: '600', 
                              color: 'var(--text-dark)'
                            }}>
                              {promotion.priority}
                            </div>
                          </div>
                        </div>

                        {/* Dates - Compact on mobile */}
                        <div style={{ 
                          padding: isSmallScreen ? '8px' : 'var(--space-2)',
                          backgroundColor: 'var(--bg-light)',
                          borderRadius: 'var(--radius-md)',
                          marginBottom: isSmallScreen ? '12px' : 'var(--space-3)',
                          fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)'
                        }}>
                          <div style={{ 
                            color: 'var(--text-light)',
                            marginBottom: '4px',
                            fontWeight: '600'
                          }}>
                            Campaign Period
                          </div>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: '1fr 1fr',
                            gap: '8px'
                          }}>
                            <div>
                              <strong>Start:</strong> {formatDate(promotion.startDate)}
                            </div>
                            <div>
                              <strong>End:</strong> {formatDate(promotion.endDate)}
                            </div>
                          </div>
                        </div>

                        {/* Stats Row */}
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          fontSize: isSmallScreen ? '9px' : 'var(--font-size-xs)',
                          color: 'var(--text-light)',
                          marginBottom: isSmallScreen ? '12px' : 'var(--space-3)',
                          padding: '8px',
                          backgroundColor: 'var(--bg-light)',
                          borderRadius: 'var(--radius-sm)'
                        }}>
                          <div>
                            <strong>{promotion.clicks || 0}</strong> Clicks
                          </div>
                          <div>
                            <strong>{promotion.impressions || 0}</strong> Views
                          </div>
                          <div>
                            <strong>{promotion.usedCount || 0}</strong> Used
                          </div>
                        </div>

                        {/* Actions - Stack on small screens */}
                        <div style={{ 
                          display: 'flex', 
                          gap: isSmallScreen ? '4px' : '6px',
                          flexWrap: 'wrap'
                        }}>
                          <button 
                            className="btn btn-secondary"
                            style={{ 
                              flex: isSmallScreen ? '1 0 calc(50% - 2px)' : '1',
                              padding: isSmallScreen ? '8px' : 'var(--space-2)',
                              fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                              minWidth: '60px'
                            }}
                            onClick={() => handleEditPromotion(promotion)}
                          >
                            Edit
                          </button>
                          
                          {promotion.status === 'active' ? (
                            <button 
                              className="btn btn-warning"
                              style={{ 
                                flex: isSmallScreen ? '1 0 calc(50% - 2px)' : '1',
                                padding: isSmallScreen ? '8px' : 'var(--space-2)',
                                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                                minWidth: '60px'
                              }}
                              onClick={() => handleStatusUpdate(promotion._id, 'inactive')}
                            >
                              Deactivate
                            </button>
                          ) : (
                            <button 
                              className="btn btn-success"
                              style={{ 
                                flex: isSmallScreen ? '1 0 calc(50% - 2px)' : '1',
                                padding: isSmallScreen ? '8px' : 'var(--space-2)',
                                fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                                minWidth: '60px'
                              }}
                              onClick={() => handleStatusUpdate(promotion._id, 'active')}
                            >
                              Activate
                            </button>
                          )}
                          
                          <button 
                            className="btn btn-danger"
                            style={{ 
                              flex: isSmallScreen ? '1 0 100%' : '1',
                              padding: isSmallScreen ? '8px' : 'var(--space-2)',
                              fontSize: isSmallScreen ? '10px' : 'var(--font-size-xs)',
                              marginTop: isSmallScreen ? '4px' : '0'
                            }}
                            onClick={() => handleDeletePromotion(promotion._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Mobile Pagination */}
            {isMobile && totalPages > 1 && (
              <div style={{
                marginTop: isSmallScreen ? 'var(--space-3)' : 'var(--space-4)',
                paddingTop: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)',
                borderTop: '1px solid var(--border-light)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: isSmallScreen ? 'var(--space-2)' : 'var(--space-3)'
              }}>
                {/* Page Info */}
                <div style={{
                  fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                  color: 'var(--text-medium)',
                  textAlign: 'center'
                }}>
                  Showing {(currentPage - 1) * promotionsPerPage + 1} to {Math.min(currentPage * promotionsPerPage, totalPromotions)} of {totalPromotions} promotions
                </div>

                {/* Pagination Controls */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isSmallScreen ? '4px' : '6px',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="btn btn-secondary"
                    style={{
                      padding: isSmallScreen ? '6px 8px' : '8px 12px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSmallScreen ? '««' : 'First'}
                  </button>

                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn btn-secondary"
                    style={{
                      padding: isSmallScreen ? '6px 8px' : '8px 12px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      opacity: currentPage === 1 ? 0.5 : 1,
                      cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSmallScreen ? '«' : 'Prev'}
                  </button>

                  <div style={{
                    display: 'flex',
                    gap: isSmallScreen ? '2px' : '4px'
                  }}>
                    {getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={pageNum === currentPage ? 'btn btn-gold' : 'btn btn-secondary'}
                        style={{
                          padding: isSmallScreen ? '6px 8px' : '8px 12px',
                          fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                          minWidth: isSmallScreen ? '32px' : '40px',
                          fontWeight: pageNum === currentPage ? '600' : '400'
                        }}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn btn-secondary"
                    style={{
                      padding: isSmallScreen ? '6px 8px' : '8px 12px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSmallScreen ? '»' : 'Next'}
                  </button>

                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="btn btn-secondary"
                    style={{
                      padding: isSmallScreen ? '6px 8px' : '8px 12px',
                      fontSize: isSmallScreen ? 'var(--font-size-xs)' : 'var(--font-size-sm)',
                      opacity: currentPage === totalPages ? 0.5 : 1,
                      cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSmallScreen ? '»»' : 'Last'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Promotion Form Modal */}
      {showForm && (
        <PromotionForm 
          promotion={editingPromotion}
          onClose={() => {
            setShowForm(false);
            setEditingPromotion(null);
          }}
          onSave={() => {
            setShowForm(false);
            setEditingPromotion(null);
            fetchPromotions();
            setCurrentPage(1);
          }}
          isMobile={isMobile}
          isSmallScreen={isSmallScreen}
        />
      )}
    </div>
  );
};

export default PromotionsManagement;